import type { tProduct } from 'src/shared/frontendTypes/product'
import type { selectedAttributes } from '../Components/AddToCart/Components/AttributesSelector'

type Attribute = NonNullable<tProduct['attributes']>[number]
type Combination = tProduct['combinations'][number]

export type DisabledAttributeOptions = Record<number, Record<number, boolean>>
export type AttributeSelectionMode = 'ordered' | 'current'

const getAttributes = (product: tProduct): Attribute[] => product.attributes || []

const getAttributeOrder = (product: tProduct) =>
  new Map(getAttributes(product).map((attribute, index) => [attribute.id, index]))

const isSelectedOption = (value: number | '' | undefined): value is number =>
  typeof value === 'number'

const hasOption = (attribute: Attribute, optionId: number) =>
  attribute.options.some(option => option.id === optionId)

const getSelectableCombinations = (product: tProduct): Combination[] =>
  product.combinations.filter(combination => combination.cultivable !== false)

export const buildDefaultSelectedAttributes = (
  product: tProduct,
): selectedAttributes => Object.fromEntries(
  getAttributes(product).map(attribute => [
    attribute.id,
    attribute.options.length === 1 ? attribute.options[0].id : '',
  ]),
) as selectedAttributes

export const isOptionSelectable = (
  product: tProduct,
  selectedAttributes: selectedAttributes,
  attributeId: number,
  optionId: number,
  mode: AttributeSelectionMode = 'ordered',
): boolean => {
  const attribute = getAttributes(product).find(item => item.id === attributeId)
  if (!attribute || !hasOption(attribute, optionId)) return false
  const attributeOrder = getAttributeOrder(product)
  const currentAttributeIndex = attributeOrder.get(attributeId) ?? Number.MAX_SAFE_INTEGER

  return getSelectableCombinations(product).some(combination => {
    const combinationOptions = combination.options || []

    if (!combinationOptions.includes(optionId)) return false

    return Object.keys(selectedAttributes).every(selectedAttributeId => {
      const selectedAttributeNumber = Number(selectedAttributeId)
      const selectedAttributeIndex = attributeOrder.get(selectedAttributeNumber)
      const selectedOption = selectedAttributes[selectedAttributeId]

      if (selectedAttributeNumber === attributeId) return true
      if (
        typeof selectedAttributeIndex !== 'number'
        || (mode === 'ordered' && selectedAttributeIndex >= currentAttributeIndex)
      ) return true
      if (!isSelectedOption(selectedOption)) return true

      return combinationOptions.includes(selectedOption)
    })
  })
}

export const buildDisabledAttributeOptions = (
  product: tProduct,
  selectedAttributes: selectedAttributes,
  mode: AttributeSelectionMode = 'ordered',
): DisabledAttributeOptions => Object.fromEntries(
  getAttributes(product).map(attribute => [
    attribute.id,
    Object.fromEntries(
      attribute.options.map(option => [
        option.id,
        !isOptionSelectable(product, selectedAttributes, attribute.id, option.id, mode),
      ]),
    ),
  ]),
) as DisabledAttributeOptions

export const normalizeSelectedAttributes = (
  product: tProduct,
  selectedAttributes: selectedAttributes,
  protectedAttributeId?: number,
): selectedAttributes => {
  const attributes = getAttributes(product)
  const next = Object.fromEntries(
    attributes.map(attribute => {
      const selectedOption = selectedAttributes[attribute.id]
      return [
        attribute.id,
        isSelectedOption(selectedOption) && hasOption(attribute, selectedOption)
          ? selectedOption
          : '',
      ]
    }),
  ) as selectedAttributes

  let changed = true

  while (changed) {
    changed = false

    for (const attribute of attributes) {
      const selectedOption = next[attribute.id]

      if (attribute.id === protectedAttributeId) continue
      if (!isSelectedOption(selectedOption)) continue
      if (isOptionSelectable(product, next, attribute.id, selectedOption)) continue

      next[attribute.id] = ''
      changed = true
    }
  }

  if (typeof protectedAttributeId === 'number') {
    const protectedOption = next[protectedAttributeId]

    if (
      isSelectedOption(protectedOption)
      && !isOptionSelectable(product, next, protectedAttributeId, protectedOption)
    ) {
      next[protectedAttributeId] = ''
    }
  }

  return next
}

export const changeSelectedAttribute = (
  product: tProduct,
  selectedAttributes: selectedAttributes,
  attributeId: number,
  optionId: number | '',
  mode: AttributeSelectionMode = 'ordered',
): selectedAttributes => {
  // The modern selector never silently clears another choice. Release that
  // choice explicitly before selecting an incompatible size or colour.
  if (mode === 'current' && optionId !== ''
    && !isOptionSelectable(product, selectedAttributes, attributeId, optionId, mode)) {
    return selectedAttributes
  }

  return normalizeSelectedAttributes(
    product,
    { ...selectedAttributes, [attributeId]: optionId },
    attributeId,
  )
}

export const buildSelectedAttributesFromOptions = (
  product: tProduct,
  options: number[],
): selectedAttributes => {
  const attributes = getAttributes(product)
  const next = buildDefaultSelectedAttributes(product)

  for (const attribute of attributes) {
    const optionId = options.find(option => hasOption(attribute, option))
    if (typeof optionId !== 'number') continue

    const candidate = {
      ...next,
      [attribute.id]: optionId,
    }

    if (isOptionSelectable(product, candidate, attribute.id, optionId)) {
      next[attribute.id] = optionId
    }
  }

  return normalizeSelectedAttributes(product, next)
}

export const buildSelectedOptionsHash = (
  product: tProduct,
  selectedAttributes: selectedAttributes,
): string => getAttributes(product)
  .map(attribute => selectedAttributes[attribute.id])
  .filter(isSelectedOption)
  .join('-')

export const selectedAttributesAreEqual = (
  left: selectedAttributes,
  right: selectedAttributes,
) => {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)])

  for (const key of keys) {
    if (left[key] !== right[key]) return false
  }

  return true
}
